import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasteListFlowHeader from "../components/PasteListFlowHeader";
import BasketSummarySection from "../components/BasketSummarySection";
import {
  clearPasteListFlow,
  fetchGroceryBasketSummary,
  loadPasteListFlow,
} from "../utils/pasteListFlow";

const emptyBasket = { rows: [], cheapest: null, savings: null, expensiveStore: null };

function resolveSummaryItems(flow) {
  if (Array.isArray(flow?.products) && flow.products.length) {
    return flow.products;
  }
  if (Array.isArray(flow?.matches)) {
    return flow.matches.map((match) => match.product).filter(Boolean);
  }
  return [];
}

function isPasteListSummaryFlow(flow, locationState) {
  return (
    locationState?.fromPasteList === true ||
    flow?.fromPasteList === true ||
    flow?.source === "paste"
  );
}

function isGroceryDetailSummaryFlow(flow, locationState) {
  if (isPasteListSummaryFlow(flow, locationState)) return false;
  return (
    locationState?.fromGrocery === true ||
    locationState?.source === "grocery_list" ||
    flow?.source === "grocery_list"
  );
}

async function resolveBasketTotals(flow) {
  if (flow?.basketTotals?.rows?.length) {
    return flow.basketTotals;
  }

  if (flow?.groceryListId) {
    const { basketTotals } = await fetchGroceryBasketSummary(flow.groceryListId);
    if (basketTotals?.rows?.length) {
      return basketTotals;
    }
  }

  return emptyBasket;
}

function PasteListSummary({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFlow = loadPasteListFlow();
  const initialPasteFlow = isPasteListSummaryFlow(initialFlow, location.state);
  const initialGroceryFlow = isGroceryDetailSummaryFlow(initialFlow, location.state);

  const [basket, setBasket] = useState(emptyBasket);
  const [status, setStatus] = useState("loading");
  const [isPasteFlow, setIsPasteFlow] = useState(initialPasteFlow);
  const [isFromGrocery, setIsFromGrocery] = useState(initialGroceryFlow);
  const [groceryListId, setGroceryListId] = useState(
    location.state?.groceryListId || initialFlow?.groceryListId || null
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const flow = loadPasteListFlow();
      const items = resolveSummaryItems(flow);
      const pasteFlow = isPasteListSummaryFlow(flow, location.state);
      const groceryFlow = isGroceryDetailSummaryFlow(flow, location.state);

      if (!items.length && !flow?.groceryListId && !pasteFlow && !groceryFlow) {
        navigate("/paste-list", { replace: true });
        return;
      }

      if (groceryFlow && !items.length && !flow?.groceryListId) {
        navigate("/settings?tab=grocery", { replace: true });
        return;
      }

      try {
        const nextBasket = await resolveBasketTotals(flow);
        if (cancelled) return;

        setBasket(nextBasket);
        setIsPasteFlow(pasteFlow);
        setIsFromGrocery(groceryFlow);
        setGroceryListId(location.state?.groceryListId || flow?.groceryListId || null);
        setStatus("succeeded");
      } catch {
        if (cancelled) return;
        setBasket(emptyBasket);
        setIsPasteFlow(pasteFlow);
        setIsFromGrocery(groceryFlow);
        setStatus("succeeded");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, location.state]);

  const handleBottomAction = () => {
    clearPasteListFlow();
    if (isFromGrocery) {
      const listQuery = groceryListId
        ? `&view=detail&grocery_list_id=${encodeURIComponent(String(groceryListId))}`
        : "";
      navigate(`/settings?tab=grocery${listQuery}`);
      return;
    }
    navigate("/paste-list");
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
      {isPasteFlow && <PasteListFlowHeader currentStep={3} />}

      <main className="mx-auto w-full max-w-6xl flex-1 px-3 py-5 sm:px-4 sm:py-6 md:px-6">
        {status === "loading" && (
          <div className="flex justify-center py-16">
            <span className="text-[16px] text-[#6E7B86]">Calculating basket totals…</span>
          </div>
        )}

        {status === "succeeded" && basket.rows?.length ? <BasketSummarySection basket={basket} /> : null}

        {status === "succeeded" && !basket.rows?.length ? (
          <div className="rounded-2xl border border-[#E6EAF0] bg-white px-4 py-8 text-center shadow-sm">
            <p className="text-[16px] font-semibold text-[#24303A]">No basket totals available yet</p>
            <p className="mt-2 text-[14px] text-[#6E7B86]">
              We could not calculate store totals from this grocery list yet. Add products with store
              prices and try again.
            </p>
            <button
              type="button"
              onClick={handleBottomAction}
              className="mt-5 w-full rounded-full bg-[#0d9499] px-6 py-2.5 text-[14px] font-semibold text-white sm:w-auto sm:py-2 sm:text-[16px]"
            >
              {isFromGrocery ? "Back to grocery list" : "Start again"}
            </button>
          </div>
        ) : null}

        {status === "succeeded" && basket.rows?.length ? (
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={handleBottomAction}
              className="w-full rounded-full bg-[#0d9499] px-6 py-2.5 text-[14px] font-semibold text-white sm:w-auto sm:py-2 sm:text-[16px]"
            >
              {isFromGrocery ? "Back to grocery list" : "Paste another list"}
            </button>
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}

export default PasteListSummary;
